import { Box, Chip, Typography } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { StatusCode } from "services/api/flagbags/types";
import { StatusLabel } from "types/flagbag";

function normalizeStatusValue(raw: unknown): string {
	if (raw === null || raw === undefined) return "";
	return String(raw).trim().toUpperCase().replace(/\s+/g, "_");
}

const finalStationColumn: GridColDef = {
	field: "finalStationName",
	headerName: "回收旗站",
	sortable: true,
	flex: 1,
	minWidth: 100,
	renderCell: (cellParams: GridRenderCellParams) => {
		const name = cellParams.value || cellParams.row.finalStationName || "-";
		return (
			<Typography
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					fontSize: "14px",
					color: "text.secondary",
				}}
			>
				{name}
			</Typography>
		);
	},
};

/** Owning / assigned station (`FlagBag.stationName`), shown right of 回收旗站. */
const stationNameColumn: GridColDef = {
	field: "stationName",
	headerName: "所屬旗站",
	sortable: true,
	flex: 1,
	minWidth: 100,
	renderCell: (cellParams: GridRenderCellParams) => {
		const name = cellParams.value || cellParams.row.stationName || "-";
		return (
			<Typography
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					fontSize: "14px",
					color: "text.secondary",
				}}
			>
				{name}
			</Typography>
		);
	},
};

export function getNormalColumns(params: {
	onStatusCellClick: (params: GridRenderCellParams) => void;
	statusLabelMap?: Record<StatusCode, string>;
}): GridColDef[] {
	return [
		{
			field: "flagbagNum",
			headerName: "編號",
			sortable: true,
			flex: 1.2,
			minWidth: 100,
		},
		{
			field: "volunteerName",
			headerName: "義工名稱",
			sortable: true,
			flex: 1,
			minWidth: 150,
			renderCell: (cellParams: GridRenderCellParams) => {
				const name = cellParams.value || "-";
				return (
					<Typography
						sx={{
							height: "100%",
							display: "flex",
							alignItems: "center",
							fontSize: "14px",
							fontWeight: 500,
						}}
					>
						{name}
					</Typography>
				);
			},
		},
		{
			field: "status",
			headerName: "狀態",
			sortable: true,
			flex: 1,
			minWidth: 120,
			renderCell: (cellParams: GridRenderCellParams) => {
				const statusValue = cellParams.value || cellParams.row.currentStatus || cellParams.row.status;
				// 调试：记录状态值（仅对未分配状态）
				if (statusValue === StatusCode.NOT_ASSIGN || statusValue === 'unassigned') {
					console.log('Status display debug:', {
						flagbagNum: cellParams.row.flagbagNum,
						statusValue: statusValue,
						statusLabelMap: params.statusLabelMap?.[statusValue as StatusCode],
						statusLabel: StatusLabel[statusValue as StatusCode],
						row: cellParams.row,
					});
				}
				// 优先使用 API 返回的状态标签映射，如果没有则使用默认的 StatusLabel
				const statusLabel = statusValue
					? (params.statusLabelMap?.[statusValue as StatusCode] || StatusLabel[statusValue as StatusCode] || "-")
					: "-";

				return (
					<Box
						onClick={(e) => {
							e.stopPropagation();
							params.onStatusCellClick(cellParams);
						}}
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							cursor: "pointer",
							"&:hover": {
								backgroundColor: "rgba(0, 0, 0, 0.04)",
							},
						}}
					>
						<Typography
							sx={{
								fontSize: "14px",
								display: "flex",
								alignItems: "center",
								gap: "4px",
							}}
						>
							{statusLabel || "-"}
							<ArrowRightIcon sx={{ fontSize: "20px", color: "text.secondary" }} />
						</Typography>
					</Box>
				);
			},
		},
		{
			field: "bagType",
			headerName: "類型",
			sortable: true,
			flex: 0.7,
			minWidth: 70,
			renderCell: (cellParams: GridRenderCellParams) => {
				const type = cellParams.value;
				let label = "-";
				let color: "default" | "primary" | "secondary" = "default";

				if (type === "volunteer") {
					label = "義工";
					color = "primary";
				} else if (type === "spare") {
					label = "後備";
					color = "secondary";
				}

				return <Chip label={label} size="small" color={color} sx={{ fontSize: "12px" }} />;
			},
		},
		finalStationColumn,
		stationNameColumn,
	];
}

/** 待同步列表：狀態欄只顯示裝置／本地資料庫狀態，不用伺服欄位。 */
export function getPendingSyncColumns(params: {
	onStatusCellClick: (params: GridRenderCellParams) => void;
	statusLabelMap?: Record<StatusCode, string>;
}): GridColDef[] {
	return [
		{
			field: "flagbagNum",
			headerName: "旗袋編號",
			sortable: true,
			flex: 1.2,
			minWidth: 120,
		},
		{
			field: "volunteerName",
			headerName: "義工名稱",
			sortable: true,
			flex: 1,
			minWidth: 100,
			renderCell: (cellParams: GridRenderCellParams) => {
				const name = cellParams.value || "-";
				return (
					<Typography
						sx={{
							height: "100%",
							display: "flex",
							alignItems: "center",
							fontSize: "14px",
							fontWeight: 500,
						}}
					>
						{name}
					</Typography>
				);
			},
		},
		{
			field: "status",
			headerName: "本地狀態",
			sortable: true,
			flex: 1,
			minWidth: 120,
			valueGetter: (_value, row) => row.currentStatus ?? row.status,
			renderCell: (cellParams: GridRenderCellParams) => {
				const statusValue = (cellParams.row.currentStatus ?? cellParams.row.status) as StatusCode | undefined;
				const statusLabel = statusValue
					? (params.statusLabelMap?.[statusValue as StatusCode] || StatusLabel[statusValue as StatusCode] || "-")
					: "-";

				return (
					<Box
						onClick={(e) => {
							e.stopPropagation();
							params.onStatusCellClick(cellParams);
						}}
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							cursor: "pointer",
							"&:hover": {
								backgroundColor: "rgba(0, 0, 0, 0.04)",
							},
						}}
					>
						<Typography
							sx={{
								fontSize: "14px",
								display: "flex",
								alignItems: "center",
								gap: "4px",
							}}
						>
							{statusLabel || "-"}
							<ArrowRightIcon sx={{ fontSize: "20px", color: "text.secondary" }} />
						</Typography>
					</Box>
				);
			},
		},
		{
			field: "bagType",
			headerName: "類型",
			sortable: true,
			flex: 0.7,
			minWidth: 70,
			renderCell: (cellParams: GridRenderCellParams) => {
				const type = cellParams.value;
				let label = "-";
				let color: "default" | "primary" | "secondary" = "default";

				if (type === "volunteer") {
					label = "義工";
					color = "primary";
				} else if (type === "spare") {
					label = "後備";
					color = "secondary";
				}

				return <Chip label={label} size="small" color={color} sx={{ fontSize: "12px" }} />;
			},
		},
		finalStationColumn,
		stationNameColumn,
	];
}

export function getPendingProcessColumns(): GridColDef[] {
	return [
		{
			field: "flagbagNum",
			headerName: "旗袋編號",
			sortable: true,
			flex: 1.2,
			minWidth: 120,
			renderCell: (cellParams: GridRenderCellParams) => (
				<Typography
					sx={{
						height: "100%",
						display: "flex",
						alignItems: "center",
						fontSize: "14px",
						fontWeight: 600,
					}}
				>
					{cellParams.value}
				</Typography>
			),
		},
		{
			field: "serverStatus",
			headerName: "伺服器數據",
			sortable: true,
			flex: 1.2,
			minWidth: 120,
			renderCell: (cellParams: GridRenderCellParams) => {
				// Prefer latest local unsynced status on row; fallback to rendered cell value.
				const rawStatus = (cellParams.row.currentStatus ||
					cellParams.row.status ||
					cellParams.value) as StatusCode | string | undefined;
				const normalized = normalizeStatusValue(rawStatus);
				const lookupKey = normalized.toLowerCase() as StatusCode;
				const statusLabel = rawStatus
					? (StatusLabel[rawStatus as StatusCode] || StatusLabel[lookupKey] || normalized)
					: "-";
				const isSyncError = statusLabel === "同步失敗";
				return (
					<Box
						sx={{
							height: "100%",
							display: "flex",
							alignItems: "center",
							gap: "4px",
							cursor: "pointer",
						}}
					>
						<Typography
							sx={{
								fontSize: "14px",
								fontWeight: 500,
								color: isSyncError ? "#666" : "#1976d2",
							}}
						>
							{statusLabel}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: "localStatus",
			headerName: "現有數據",
			sortable: true,
			flex: 1.2,
			minWidth: 120,
			renderCell: (cellParams: GridRenderCellParams) => {
				const rawStatus = cellParams.value as StatusCode | string | undefined;
				const normalized = normalizeStatusValue(rawStatus);
				const lookupKey = normalized.toLowerCase() as StatusCode;
				const statusLabel = rawStatus
					? (StatusLabel[rawStatus as StatusCode] || StatusLabel[lookupKey] || normalized)
					: "-";
				return (
					<Box
						sx={{
							height: "100%",
							display: "flex",
							alignItems: "center",
							gap: "4px",
							cursor: "pointer",
						}}
					>
						<Typography
							sx={{
								fontSize: "14px",
								fontWeight: 500,
								color: "#d32f2f",
							}}
						>
							{statusLabel}
						</Typography>
						<ArrowRightIcon sx={{ fontSize: "20px", color: "text.secondary" }} />
					</Box>
				);
			},
		},
		finalStationColumn,
		stationNameColumn,
	];
}


